import { useApolloClient } from '@apollo/client';

import { useApiClient } from '@eluve/api-client-provider';
import {
  ScheduleBackgroundTaskAction,
  useTaskProcessor,
} from '@eluve/appointment-tasks';
import { FCC, useToast } from '@eluve/components';
import { useTenantUserSettings } from '@eluve/frontend-feature-user-settings';
import {
  ScheduleFileUploadAction,
  audioFileSystem,
  createPrepareAppointmentAudioRecordsActor,
  mediaRecorder,
  noopAudioFileSystem,
  transcriptionMachine,
  webWorkerAudioActor,
} from '@eluve/frontend-machine-common';
import { PlatformTypesLookup } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import {
  useFeatureFlagPayload,
  useIsV2AudioStackEnabled,
} from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { sessionSoundPlayer } from '@eluve/sounds';
import {
  useDb,
  usePeriodicRemoteFileSyncActor,
  useUserFileSystemStore,
} from '@eluve/user-local-files';

import { appConfig } from '../../../../../../../config';

import { getAuthToken } from './actors/getAuthToken';
import { ActiveAppointmentContextProvider } from './components/ActiveAppointmentContextProvider';
import { MicrophoneFailedDialog } from './components/MicrophoneFailedDialog';
import { RecordingFailedDialog } from './components/RecordingFailedDialog';
import { TranscriptionSounds } from './components/TranscriptionSounds';
import { TranscriptionStateTransitionLogger } from './components/TranscriptionStateTransitionLogger';
import { UserPresenceDialog } from './components/UserPresenceDialog';
import { TranscriptionMachineContext } from './hooks/useTranscriptionMachine';

export const TranscriptionMachineProvider: FCC = ({ children }) => {
  const toast = useToast();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();
  const apolloClient = useApolloClient();
  const { settings } = useTenantUserSettings();
  const logger = useNamedLogger('TranscriptionMachine');
  const db = useDb();
  const apiClient = useApiClient();

  const { taskProcessor } = useTaskProcessor();
  const { send: sendToPeriodicRemoteFileSyncMachine } =
    usePeriodicRemoteFileSyncActor();

  const chunkedBatchTranscriptionResponse = useFeatureFlagPayload(
    'CHUNKED_BATCH_TRANSCRIPTION_DURATION_MINUTES',
  );

  const chunkedBatchTranscriptionEnabled = useIsFeatureFlagEnabled(
    'CHUNKED_BATCH_TRANSCRIPTION',
  );

  const chunkDurationMinutes =
    chunkedBatchTranscriptionResponse?.chunkDurationMinutes;

  const useV2AudioStack = useIsV2AudioStackEnabled();

  sessionSoundPlayer.setIsEnabled(settings?.enableSoundEffects ?? true);

  const userFileSystem = useUserFileSystemStore(
    (state) => state.userFileSystem,
  );

  const scheduleFileUpload: ScheduleFileUploadAction = (_, uploadFileEvent) => {
    const { fileName, ...rest } = uploadFileEvent;
    // TODO(grigor)[ENG-2216] - Properly handle lock cleanup in transcription machine when actor cleanup happens
    setTimeout(() => {
      sendToPeriodicRemoteFileSyncMachine({
        type: 'IMMEDIATE_SYNC_AVAILABLE',
        file: {
          id: fileName,
          attemptCount: 0,
          lastUploadAttemptedAt: new Date().toISOString(),
          ...rest,
        },
      });
    }, 50);
  };

  const scheduleBackgroundTask: ScheduleBackgroundTaskAction = (_, task) =>
    taskProcessor.addTask(task);

  return (
    <TranscriptionMachineContext.Provider
      logic={transcriptionMachine.provide({
        actions: {
          toast: (_, { type, message, id }) => {
            toast[type](message, {
              id: id,
            });
          },
          scheduleFileUpload,
          scheduleBackgroundTask,
        },
        actors: {
          getAuthToken: getAuthToken,
          prepareAppointmentAudioRecords:
            createPrepareAppointmentAudioRecordsActor({
              apiClient,
              platform: PlatformTypesLookup.WEB,
              platformId: 'web-vite',
              platformVersion: appConfig.VITE_APP_VERSION,
            }),
          mediaRecorder: useV2AudioStack ? webWorkerAudioActor : mediaRecorder,
          audioFileSystem: useV2AudioStack
            ? noopAudioFileSystem
            : audioFileSystem,
        },
      })}
      options={{
        input: {
          apiEndpoint: appConfig.VITE_API_DOMAIN,
          tenantId: tenantId ?? '',
          userId,
          userFileSystem,
          apolloClient,
          logger,
          db,
          chunkedBatchTranscriptionEnabled,
          chunkDurationMinutes,
          isUsingWebWorkerAudioRecording: useV2AudioStack,
          isUsingResumableUploads: useV2AudioStack,
          isUsingLiveTranscription: true,
          datadogEnv: appConfig.VITE_METRICS_ENV,
          serviceName: 'web-vite',
        },
      }}
    >
      <ActiveAppointmentContextProvider>
        {children}
        <TranscriptionSounds />
        <MicrophoneFailedDialog />
        <RecordingFailedDialog />
        <TranscriptionStateTransitionLogger />
        <UserPresenceDialog />
      </ActiveAppointmentContextProvider>
    </TranscriptionMachineContext.Provider>
  );
};
